/**
 * Client-side Translation Service
 * ===============================
 * Fallback translation service that runs in the browser when server-side
 * Google Translate API is not available or fails.
 *
 * Uses a simplified approach with basic language mappings for common phrases.
 * For production, consider integrating with a client-side translation service.
 *
 * @module utils/clientTranslation
 */

import { DEFAULT_LANGUAGE } from '../i18n';

// Basic translation mappings for common UI phrases
const TRANSLATION_MAP = {
  // German
  de: {
    'Home': 'Startseite',
    'About Us': 'Über uns',
    'Contact': 'Kontakt',
    'Packages': 'Pakete',
    'Destinations': 'Ziele',
    'Blog': 'Blog',
    'Search': 'Suche',
    'Login': 'Anmelden',
    'Register': 'Registrieren',
    'Logout': 'Abmelden',
    'Welcome': 'Willkommen',
    'Loading...': 'Laden...',
    'Error': 'Fehler',
    'Success': 'Erfolg',
    'Submit': 'Senden',
    'Cancel': 'Abbrechen',
    'Close': 'Schließen',
    'Open': 'Öffnen',
    'Menu': 'Menü',
    'Settings': 'Einstellungen',
  },

  // French
  fr: {
    'Home': 'Accueil',
    'About Us': 'À propos',
    'Contact': 'Contact',
    'Packages': 'Forfaits',
    'Destinations': 'Destinations',
    'Blog': 'Blog',
    'Search': 'Rechercher',
    'Login': 'Se connecter',
    'Register': 'S\'inscrire',
    'Logout': 'Se déconnecter',
    'Welcome': 'Bienvenue',
    'Loading...': 'Chargement...',
    'Error': 'Erreur',
    'Success': 'Succès',
    'Submit': 'Soumettre',
    'Cancel': 'Annuler',
    'Close': 'Fermer',
    'Open': 'Ouvrir',
    'Menu': 'Menu',
    'Settings': 'Paramètres',
  },

  // Italian
  it: {
    'Home': 'Home',
    'About Us': 'Chi siamo',
    'Contact': 'Contatto',
    'Packages': 'Pacchetti',
    'Destinations': 'Destinazioni',
    'Blog': 'Blog',
    'Search': 'Cerca',
    'Login': 'Accedi',
    'Register': 'Registrati',
    'Logout': 'Esci',
    'Welcome': 'Benvenuto',
    'Loading...': 'Caricamento...',
    'Error': 'Errore',
    'Success': 'Successo',
    'Submit': 'Invia',
    'Cancel': 'Annulla',
    'Close': 'Chiudi',
    'Open': 'Apri',
    'Menu': 'Menu',
    'Settings': 'Impostazioni',
  },

  // Spanish
  es: {
    'Home': 'Inicio',
    'About Us': 'Sobre nosotros',
    'Contact': 'Contacto',
    'Packages': 'Paquetes',
    'Destinations': 'Destinos',
    'Blog': 'Blog',
    'Search': 'Buscar',
    'Login': 'Iniciar sesión',
    'Register': 'Registrarse',
    'Logout': 'Cerrar sesión',
    'Welcome': 'Bienvenido',
    'Loading...': 'Cargando...',
    'Error': 'Error',
    'Success': 'Éxito',
    'Submit': 'Enviar',
    'Cancel': 'Cancelar',
    'Close': 'Cerrar',
    'Open': 'Abrir',
    'Menu': 'Menú',
    'Settings': 'Configuración',
  },

  // Swahili
  sw: {
    'Home': 'Nyumbani',
    'About Us': 'Kuhusu sisi',
    'Contact': 'Wasiliana',
    'Packages': 'Vifurushi',
    'Destinations': 'Maeneo ya kusafiri',
    'Blog': 'Blogu',
    'Search': 'Tafuta',
    'Login': 'Ingia',
    'Register': 'Jisajili',
    'Logout': 'Toka',
    'Welcome': 'Karibu',
    'Loading...': 'Inapakia...',
    'Error': 'Kosa',
    'Success': 'Mafanikio',
    'Submit': 'Wasilisha',
    'Cancel': 'Ghairi',
    'Close': 'Funga',
    'Open': 'Fungua',
    'Menu': 'Menyu',
    'Settings': 'Mipangilio',
  },

  // Chinese
  zh: {
    'Home': '首页',
    'About Us': '关于我们',
    'Contact': '联系我们',
    'Packages': '套餐',
    'Destinations': '目的地',
    'Blog': '博客',
    'Search': '搜索',
    'Login': '登录',
    'Register': '注册',
    'Logout': '退出',
    'Welcome': '欢迎',
    'Loading...': '加载中...',
    'Error': '错误',
    'Success': '成功',
    'Submit': '提交',
    'Cancel': '取消',
    'Close': '关闭',
    'Open': '打开',
    'Menu': '菜单',
    'Settings': '设置',
  },
};

/**
 * Translate text using client-side fallback mappings
 * @param {string} text - Text to translate
 * @param {string} targetLang - Target language code
 * @returns {string} Translated text or original text if no translation available
 */
export function translateTextClient(text, targetLang) {
  if (!text || targetLang === DEFAULT_LANGUAGE) {
    return text;
  }

  const langMap = TRANSLATION_MAP[targetLang];
  if (!langMap) {
    return text;
  }

  return langMap[text] || text;
}

/**
 * Check if server-side translation is available
 * @returns {boolean} True if server translation should work
 */
export function isServerTranslationAvailable() {
  // This is a simple check - in production you might want to check
  // an API endpoint or configuration
  return true; // Assume it's available unless we know otherwise
}

/**
 * Get translation source information
 * @param {string} currentLang - Current language
 * @returns {object} Translation source info
 */
export function getTranslationSource(currentLang) {
  if (currentLang === DEFAULT_LANGUAGE) {
    return { source: 'native', available: true };
  }

  // Check if we have server-side translations
  const serverAvailable = isServerTranslationAvailable();

  if (serverAvailable) {
    return { source: 'server', available: true };
  }

  // Check if we have client-side translations
  const hasClientTranslations = TRANSLATION_MAP[currentLang];
  if (hasClientTranslations) {
    return { source: 'client', available: true };
  }

  return { source: 'fallback', available: false };
}